import { createContext } from 'react';
import { useState, useContext, ReactNode } from 'react';
import { SelectedAppType } from '../components/apps/AppBulkEdit/types/appBulkEditTypes';

interface SelectedAppProviderProps {
  children: ReactNode;
}

interface SelectedAppContextType {
  selectedApps: SelectedAppType[];
  addApp: (app: SelectedAppType) => void;
  removeApp: (applicationId: number) => void;
  addMultipleApps: (apps: SelectedAppType[]) => void;
}

const SelectedAppContext = createContext<SelectedAppContextType | undefined>(
  undefined,
);

/**
 * Provides the selected apps context to its children.
 * @param SelectedAppProviderProps props - The props for the provider.
 */
export const SelectedAppProvider = ({
  children,
}: SelectedAppProviderProps): JSX.Element => {
  const [selectedApps, setSelectedApps] = useState<SelectedAppType[]>([]);

  const addApp = (app: SelectedAppType) => {
    setSelectedApps(prevApps => {
      const exists = prevApps.some(a => a.applicationId === app.applicationId);
      return exists ? prevApps : [...prevApps, app];
    });
  };

  const removeApp = (applicationId: number) => {
    setSelectedApps(prevApps =>
      prevApps.filter(app => app.applicationId !== applicationId),
    );
  };

  const addMultipleApps = (apps: SelectedAppType[]) => {
    setSelectedApps(apps);
  };

  return (
    <SelectedAppContext.Provider
      value={{
        selectedApps,
        addApp,
        removeApp,
        addMultipleApps,
      }}
    >
      {children}
    </SelectedAppContext.Provider>
  );
};

/**
 * Custom hook to use the SelectedAppContext.
 * @returns SelectedAppContextType The context value.
 */
export const useSelectedAppContext = (): SelectedAppContextType => {
  const context = useContext(SelectedAppContext);
  if (!context) {
    throw new Error(
      'useSelectedAppContext must be used within a SelectedAppProvider',
    );
  }
  return context;
};
